import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon, Clock, User, Phone, MessageSquare, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const timeSlots = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
  '17:00', '17:30', '18:00'
];

const durationOptions = [
  { value: 30, label: '30 Minuten' },
  { value: 60, label: '1 Stunde' },
  { value: 90, label: '1,5 Stunden' },
  { value: 120, label: '2 Stunden' },
  { value: 150, label: '2,5 Stunden' },
  { value: 180, label: '3 Stunden' },
];

export const AppointmentForm = ({ onSubmit, isLoading }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState();
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('60');
  const [service, setService] = useState('');
  const [errors, setErrors] = useState({});
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!name || name.length < 2) newErrors.name = 'Name muss mindestens 2 Zeichen lang sein';
    if (!phone) newErrors.phone = 'Telefonnummer ist erforderlich';
    if (!date) newErrors.date = 'Datum ist erforderlich';
    if (!time) newErrors.time = 'Uhrzeit ist erforderlich';
    if (!duration) newErrors.duration = 'Dauer ist erforderlich';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const formattedDate = format(date, 'yyyy-MM-dd');
    onSubmit({
      name,
      phone,
      date: formattedDate,
      time,
      duration: parseInt(duration),
      service: service || null,
    });

    // Reset form
    setName('');
    setPhone('');
    setDate(undefined);
    setTime('');
    setDuration('60');
    setService('');
    setErrors({});
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="appointment-form">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <User className="w-4 h-4" />
            Kundenname
          </Label>
          <Input
            id="name"
            data-testid="name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-secondary border-border focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground h-12 text-base transition-all duration-200"
            placeholder="Max Mustermann"
          />
          {errors.name && <p className="text-destructive text-xs">{errors.name}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Telefonnummer
          </Label>
          <Input
            id="phone"
            data-testid="phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="bg-secondary border-border focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground font-mono h-12 text-base transition-all duration-200"
            placeholder="+49 176 12345678"
            type="tel"
          />
          {errors.phone && <p className="text-destructive text-xs">{errors.phone}</p>}
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <CalendarIcon className="w-4 h-4" />
            Datum
          </Label>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                data-testid="date-picker-button"
                variant="outline"
                className="w-full justify-start text-left font-normal bg-secondary border-border hover:border-primary hover:bg-secondary/80 h-12 text-base transition-all duration-200"
              >
                <CalendarIcon className="mr-2 h-5 w-5" />
                {date ? format(date, 'dd.MM.yyyy', { locale: de }) : 'Datum wählen'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(selectedDate) => {
                  setDate(selectedDate);
                  setDatePickerOpen(false);
                }}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
                className="bg-card"
              />
            </PopoverContent>
          </Popover>
          {errors.date && <p className="text-destructive text-xs">{errors.date}</p>}
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Uhrzeit
          </Label>
          <Select value={time} onValueChange={setTime}>
            <SelectTrigger data-testid="time-select" className="bg-secondary border-border focus:border-primary focus:ring-2 focus:ring-primary/20 h-12 text-base">
              <SelectValue placeholder="Uhrzeit wählen" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border max-h-60">
              {timeSlots.map((slot) => (
                <SelectItem key={slot} value={slot} className="hover:bg-secondary focus:bg-secondary text-base">
                  {slot} Uhr
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.time && <p className="text-destructive text-xs">{errors.time}</p>}
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Dauer
          </Label>
          <Select value={duration} onValueChange={setDuration}>
            <SelectTrigger data-testid="duration-select" className="bg-secondary border-border focus:border-primary focus:ring-2 focus:ring-primary/20 h-12 text-base">
              <SelectValue placeholder="Dauer wählen" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {durationOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value.toString()} className="hover:bg-secondary focus:bg-secondary text-base">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.duration && <p className="text-destructive text-xs">{errors.duration}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="service" className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Leistung / Nachricht
          </Label>
          <Textarea
            id="service"
            data-testid="service-textarea"
            value={service}
            onChange={(e) => setService(e.target.value)}
            className="bg-secondary border-border focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground min-h-[100px] text-base transition-all duration-200"
            placeholder="z.B. Stage 1 Chiptuning für BMW 320d"
          />
        </div>
      </div>

      <Button
        type="submit"
        data-testid="appointment-form-submit-button"
        disabled={isLoading}
        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 text-base rounded transition-all duration-200 hover:shadow-glow-red active:scale-[0.98]"
      >
        <Plus className="w-5 h-5 mr-2" />
        {isLoading ? 'Wird erstellt...' : 'Termin erstellen'}
      </Button>
    </form>
  );
};